<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'

import { insightActionsApi } from '../api/InsightActionsApi'
import { useInsightActionScope } from '../composables/useInsightActionScope'
import type { InsightActionOrigin } from '../models/insightAction.types'
import { adaptOrigin } from '../service/adaptRun'
import { insightDeepLink } from '../service/insightLink'

import { useI18n } from '@/shared/i18n'

const props = defineProps<{ requestId: string }>()

const { t } = useI18n()
const scope = useInsightActionScope()
const origin = ref<InsightActionOrigin | null>(null)

const link = computed(() => (origin.value ? insightDeepLink(origin.value.insightId) : null))

const who = computed(
  () => origin.value?.requestedBy?.displayName || t('insightActions.history.requesterRemoved'),
)

watch(
  () => [props.requestId, scope.accountId.value, scope.workspaceId.value] as const,
  async ([requestId, accountId, workspaceId]) => {
    origin.value = null
    if (!requestId || accountId === null || workspaceId === null) return
    try {
      origin.value = adaptOrigin(
        await insightActionsApi.lookupOrigin(accountId, workspaceId, requestId),
      )
    } catch {
      origin.value = null
    }
  },
  { immediate: true },
)
</script>

<template>
  <section
    v-if="origin"
    class="change-origin rounded-md border border-subtle px-md py-sm"
    data-testid="change-origin"
  >
    <span class="text-2xs font-semibold uppercase tracking-wide text-muted">
      {{ t('insightActions.origin.label') }}
    </span>
    <RouterLink
      v-if="link"
      :to="link"
      class="mt-2xs block font-semibold"
      data-testid="origin-insight-link"
    >
      {{ origin.insightTitle }}
    </RouterLink>
    <span v-else class="mt-2xs block font-semibold">{{ origin.insightTitle }}</span>
    <p v-if="origin.rowSummary" class="mt-2xs mb-0 text-sm text-muted">{{ origin.rowSummary }}</p>
    <p class="mt-2xs mb-0 text-xs text-muted">
      {{ t('insightActions.origin.meta', { config: origin.configLabel, who }) }}
    </p>
  </section>
</template>
